import { useCallback, useEffect, useRef, useState } from "react";

import * as requests from "../../services/api/requestState";

export interface LoaderProps<P, T> {
	endpoint: (params: P) => Promise<T>;
	onError?: (err: unknown, parameters: P) => void;
	onSuccess?: (result: T, parameters: P) => void;
	refreshBeforeLoading?: boolean;
}

export interface LoaderHandle<P, T> {
	request: requests.RequestState<T>;
	load: (params: P) => Promise<T>;
	reset: () => void;
}

export const useLoader = <P, T>(props: LoaderProps<P, T>): LoaderHandle<P, T> => {
	const { endpoint, onSuccess, onError, refreshBeforeLoading } = props;
	const [request, setRequestState] = useState(requests.empty<T>());

	const aborted = useRef(false);

	const abort = useCallback(() => {
		aborted.current = true;
	}, []);

	useEffect(() => {
		if (aborted.current) {
			// This instance of this hook has been unmounted, but is still being used.
			// this should only happen when the hook
			// is remounted by HMR, in other cases a new instance would get created.
			// reset abort flag.
			console.debug("useEffect after unmount, should only happen in HMR scenarios");
			aborted.current = false;
		}
		return abort;
	}, [abort]);

	const load = useCallback(
		async (parameters: P): Promise<T> => {
			if (!aborted.current) {
				setRequestState(s => requests.load(undefined, refreshBeforeLoading ? undefined : s.data));
			}
			try {
				const data = await endpoint(parameters);
				if (!aborted.current) {
					setRequestState(requests.success(data));
					onSuccess && onSuccess(data, parameters);
				}
				return data;
			} catch (error) {
				if (!aborted.current) {
					const errorMessage = error instanceof Error ? error.message : typeof error === "string" ? error : "";
					setRequestState(requests.error(errorMessage));
					onError && onError(error, parameters);
				}
				throw error;
			}
		},
		[endpoint, onSuccess, onError, refreshBeforeLoading],
	);

	const reset = useCallback(() => {
		if (!aborted.current) {
			setRequestState(requests.empty());
		}
	}, []);

	return { request, load, reset };
};
